import { createTheme, type PaletteMode } from '@mui/material/styles';

export type WaveThemeMode = 'light' | 'dark';

/**
 * Creates a dynamic Wave theme based on the specified mode
 * Erdos Miller branded theme optimized for data visualization
 * Based on Material UI best practices for high contrast and extended use
 */
export const createWaveTheme = (mode: WaveThemeMode = 'dark') => createTheme({
  palette: {
    mode: mode as PaletteMode,
    primary: {
      // Light blue for dark mode, darker blue for light mode
      main: mode === 'dark' ? '#90caf9' : '#1976d2',
      light: mode === 'dark' ? '#c3f0ff' : '#42a5f5',
      dark: mode === 'dark' ? '#5d99c6' : '#1565c0',
    },
    secondary: {
      // Pink accent works well in both modes
      main: mode === 'dark' ? '#f48fb1' : '#e91e63',
      light: mode === 'dark' ? '#ffc1e3' : '#f06292',
      dark: mode === 'dark' ? '#bf5f82' : '#c2185b',
    },
    // Let Material UI auto-generate background and text colors based on mode
    // This ensures proper contrast and accessibility
    
    // High contrast colors for data visualization
    info: {
      main: mode === 'dark' ? '#29b6f6' : '#0288d1',
    },
    success: {
      main: mode === 'dark' ? '#66bb6a' : '#388e3c',
    },
    warning: {
      main: mode === 'dark' ? '#ffa726' : '#f57c00',
    },
    error: {
      main: mode === 'dark' ? '#f44336' : '#d32f2f',
    },
  },
  typography: {
    // Compact sizing for data-dense interfaces
    fontSize: 14,
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2rem',
      fontWeight: 500,
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 500,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 500,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 500,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
    },
    body1: {
      fontSize: '14px',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '12px',
      lineHeight: 1.4,
    },
  },
  spacing: 8, // Default 8px spacing unit
  shape: {
    borderRadius: 4,
  },
  components: {
    // Global component overrides for compact, professional UI
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // Theme-aware scrollbar colors
          scrollbarColor: mode === 'dark' 
            ? '#6b6b6b #2b2b2b' 
            : '#bdbdbd #f5f5f5',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            backgroundColor: mode === 'dark' ? '#2b2b2b' : '#f5f5f5',
            width: 8,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: mode === 'dark' ? '#6b6b6b' : '#bdbdbd',
            minHeight: 24,
          },
          '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
            backgroundColor: mode === 'dark' ? '#959595' : '#9e9e9e',
          },
          '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
            backgroundColor: mode === 'dark' ? '#959595' : '#9e9e9e',
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: mode === 'dark' ? '#959595' : '#9e9e9e',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none', // Remove uppercase transformation
          padding: '6px 16px',
          borderRadius: 6,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Remove default gradient
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          // Use theme's paper background for consistency
          backgroundColor: mode === 'dark' ? '#1e1e1e' : '#ffffff',
          boxShadow: mode === 'dark' 
            ? '0 1px 3px rgba(0,0,0,0.3)' 
            : '0 1px 3px rgba(0,0,0,0.12)',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '48px !important', // Compact toolbar
          paddingLeft: '16px',
          paddingRight: '16px',
        },
      },
    },
  },
});

// Create default dark theme for backward compatibility
export const theme = createWaveTheme('dark');

// Export the factory as default for new usage
export default createWaveTheme; 